/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  ButtonGroup,
  FormControlLabel,
  Switch,
  Box,
  IconButton,
  Typography,
  Popper,
  ClickAwayListener,
} from '@mui/material';
import { HiroLense, HiroUpdatingType } from '../../../../types';
import { LENSE_LABELS, LenseToCfg } from '../../../../config';
import { useRecoilValue } from 'recoil';
import { hiroUpdatingTypeState } from '../../../../states';
import { useHiroCtx } from '../../HiroProvider';
import Button from '@mui/material/Button';
import { SGTooltip } from '../../../core';
import { SGSelect } from '../../../shared';
import { IndicatorsButton } from './Indicators';
import { ChartTypeButton } from './ChartTypeButton';
import { useState, useCallback } from 'react';

import { useTheme } from '@mui/material/styles';
import { dayjs, isMarketOpenOnDate, ET } from 'util/shared/date';

const CANDLE_DURATION_OPTIONS = [5, 10, 30, 60, 120, 300, 600, 900, 1_800];

const formatCandleDuration = (duration: number) =>
  duration >= 60 ? `${duration / 60}m` : `${duration}s`;

const CandleDurationButton = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { candleDuration, update } = useHiroCtx();

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => setOpen(false), []);

  const optionSx = (active: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: '10px',
    py: '7px',
    borderRadius: '8px',
    cursor: 'pointer',
    border: `1px solid ${active ? theme.palette.primary.main : 'rgba(255,255,255,0.08)'}`,
    bgcolor: active ? 'rgba(24,189,172,0.1)' : 'transparent',
    color: active ? theme.palette.primary.main : 'text.secondary',
    transition: 'all 0.15s',
    '&:hover': {
      bgcolor: active ? 'rgba(24,189,172,0.14)' : 'rgba(255,255,255,0.04)',
      borderColor: active
        ? theme.palette.primary.main
        : 'rgba(255,255,255,0.18)',
      color: active ? theme.palette.primary.main : 'text.primary',
    },
  });

  return (
    <>
      <SGTooltip title="Candle duration" placement="top">
        <IconButton
          onClick={handleClick}
          size="medium"
          sx={{ color: theme.palette.primary.main }}
        >
          <Typography
            sx={{
              color: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'Satoshi Complete',
            }}
          >
            {formatCandleDuration(candleDuration)}
          </Typography>
        </IconButton>
      </SGTooltip>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: 1_298 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Box
            sx={{
              mt: '8px',
              width: '160px',
              backgroundColor: theme.palette.background.paper,
              borderRadius: '14px',
              boxShadow:
                '0 8px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: '14px',
                py: '10px',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                background:
                  'linear-gradient(135deg, rgba(24,189,172,0.07) 0%, transparent 60%)',
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'Satoshi Complete',
                  color: 'text.primary',
                }}
              >
                Candle Duration
              </Typography>
            </Box>

            <Box
              sx={{
                p: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
              }}
            >
              {CANDLE_DURATION_OPTIONS.map((d) => (
                <Box
                  key={d}
                  onClick={() => {
                    update({ candleDuration: d });
                    handleClose();
                  }}
                  sx={optionSx(candleDuration === d)}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontFamily: 'SF Pro Display',
                      fontWeight: candleDuration === d ? 600 : 400,
                      color: 'inherit',
                    }}
                  >
                    {formatCandleDuration(d)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontFamily: 'SF Pro Display',
                      color: 'text.disabled',
                    }}
                  >
                    {d >= 60
                      ? `${d / 60} minute${d <= 60 ? '' : 's'}`
                      : `${d} seconds`}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

type HiroInlineControlsProps = {
  isInline: boolean;
};

const LENSE_LABELS_TO_TOOLTIP = {
  [HiroLense.All]: 'Show all options trades',
  [HiroLense.NextExp]:
    'Show options trades for the next available expiration for this symbol',
  [HiroLense.Retail]: 'Show options trades we think are from retail investors',
};

export const PutCallButton = () => {
  const { optionType, update } = useHiroCtx();
  return (
    <SGTooltip
      title="Show combined HIRO value or split by puts/calls"
      placement="top"
    >
      <ButtonGroup
        variant="outlined"
        aria-label="outlined secondary button group"
        sx={{
          alignSelf: 'center',
          height: '28px',
          width: '96px',
        }}
      >
        <Button
          sx={{
            fontFamily: 'SF Pro Display',
            fontSize: 12,
            textTransform: 'capitalize',
            backgroundColor:
              optionType === 'total'
                ? 'rgba(24, 189, 172, 0.25)'
                : 'transparent',
            whiteSpace: 'nowrap',
            textAlign: 'center',
          }}
          onClick={() => {
            update({ optionType: 'total' });
          }}
        >
          Total
        </Button>
        <Button
          sx={{
            fontFamily: 'SF Pro Display',
            fontSize: 12,
            textTransform: 'capitalize',
            backgroundColor:
              optionType === 'pc' ? 'rgba(24, 189, 172, 0.25)' : 'transparent',
            whiteSpace: 'nowrap',
            textAlign: 'center',
          }}
          onClick={() => {
            update({ optionType: 'pc' });
          }}
        >
          Put/Call
        </Button>
      </ButtonGroup>
    </SGTooltip>
  );
};

export const HiroInlineControls = ({ isInline }: HiroInlineControlsProps) => {
  const {
    selectedLenses,
    candleDuration,
    enableHiroV3,
    update,
    includeHiroETH: showExtendedHours,
  } = useHiroCtx();
  const hiroUpdatingType = useRecoilValue(hiroUpdatingTypeState);

  const lenses = isInline
    ? [HiroLense.All, HiroLense.NextExp]
    : Object.values(HiroLense);
  const controlSlotProps = {
    typography: {
      fontSize: '12px',
    },
  };

  return (
    <>
      {hiroUpdatingType === HiroUpdatingType.STREAMING && (
        <SGSelect
          key={'candle_duration'}
          label={'Candle Duration'}
          multiple={false}
          value={candleDuration}
          setter={(newVal: number) =>
            update({
              candleDuration: newVal,
            })
          }
          options={[5, 10, 30, 60, 120, 300, 600, 900, 1_800]}
          optionsTextTransform={(duration: any) =>
            duration >= 60 ? `${duration / 60}m` : `${duration}s`
          }
          tooltipTransform={(duration: any) =>
            `Set the duration of each HIRO candle to ${
              duration >= 60
                ? `${duration / 60} minute${duration <= 60 ? '' : 's'}`
                : `${duration} seconds`
            }`
          }
          sx={{
            width: '43px',
            height: '27px',
            textAlign: 'center',
            alignSelf: 'center',
            '.MuiSelect-select': {
              paddingLeft: '6px !important',
              paddingRight: '15px !important',
            },
            marginLeft: '5px',
          }}
          iconSx={{ marginRight: '-8px !important' }}
          selectTooltipProps={{
            placement: 'bottom',
            title: 'Customize the duration of each HIRO candle.',
          }}
        />
      )}
      {lenses
        .filter((l) => !enableHiroV3 || l != HiroLense.Retail)
        .map((lense) => {
          return (
            <SGTooltip
              title={LENSE_LABELS_TO_TOOLTIP[lense] ?? ''}
              placement="top"
              key={lense}
            >
              <FormControlLabel
                slotProps={controlSlotProps}
                value="bottom"
                sx={{
                  marginLeft: '2px',
                  marginRight: '2px',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                }}
                control={
                  <Switch
                    size="small"
                    color={LenseToCfg[lense].check}
                    checked={selectedLenses[lense] === 1}
                    onChange={(e) => {
                      const newSelectedLenses = {
                        ...selectedLenses,
                        [lense]: e.target.checked ? 1 : 0,
                      };
                      update({
                        selectedLenses: newSelectedLenses,
                      });
                    }}
                  />
                }
                label={LENSE_LABELS[lense]}
                labelPlacement="end"
              />
            </SGTooltip>
          );
        })}
      {isInline && <PutCallButton />}
      {enableHiroV3 &&
        isInline && [
          <IndicatorsButton key="indicators" />,
          <ChartTypeButton key="chart-type" />,
          <CandleDurationButton key="candle-duration" />,
          <RecentDaysSelector key="RecentDaysSelector" />,
          <SGTooltip
            title="Extended Hours"
            placement="top"
            key="extended-hours"
          >
            <FormControlLabel
              value="bottom"
              sx={{
                marginLeft: '2px',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                marginRight: '2ex',
              }}
              control={
                <Switch
                  size="small"
                  checked={showExtendedHours}
                  onChange={(e) => update({ includeHiroETH: e.target.checked })}
                />
              }
              label="Extended Hours"
              labelPlacement="end"
            />
          </SGTooltip>,
        ]}
    </>
  );
};

const RecentDaysSelector = () => {
  const { update } = useHiroCtx();
  return (
    <ButtonGroup
      variant="outlined"
      aria-label="outlined secondary button group"
      sx={{
        alignSelf: 'center',
        height: '28px',
        minWidth: '3em',
        maxWidth: '2em',
        marginRight: '2ex',
      }}
    >
      <Button
        sx={{
          fontFamily: 'SF Pro Display',
          fontSize: 12,
          textTransform: 'capitalize',
          whiteSpace: 'nowrap',
          textAlign: 'center',
          minWidth: '1em!important',
        }}
        title="Display the last day"
        onClick={() => {
          let to = dayjs().tz(ET);
          while (!isMarketOpenOnDate(to)) {
            to = to.subtract(1, 'day');
          }
          update({ startQueryDate: to, endQueryDate: to });
        }}
      >
        1d
      </Button>
      <Button
        sx={{
          fontFamily: 'SF Pro Display',
          fontSize: 12,
          textTransform: 'capitalize',
          whiteSpace: 'nowrap',
          textAlign: 'center',
          minWidth: '1em!important',
        }}
        title="Display the last 5 days"
        onClick={() => {
          let to = dayjs().tz(ET);
          while (!isMarketOpenOnDate(to)) {
            to = to.subtract(1, 'day');
          }
          let from = to;
          for (let i = 0; i < 5; ++i) {
            from = from.subtract(1, 'day');
            while (!isMarketOpenOnDate(from)) {
              from = from.subtract(1, 'day');
            }
          }
          update({ startQueryDate: from, endQueryDate: to });
        }}
      >
        5d
      </Button>
    </ButtonGroup>
  );
};
